import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";
import { cn } from "@/lib/utils";

type PublicationType = "all" | "free" | "paid";
type PublicationStatus = "all" | "active" | "deleted";

type Publication = {
  id: string;
  photo_url: string;
  description: string;
  invitation_type: string;
  invitation_count: number;
  is_published: boolean;
  status: "active" | "deleted";
  author_id: string;
  author_name: string;
  author_email: string;
  created_at: string;
  updated_at: string;
};

type ListResponse = {
  publications: Publication[];
  total: number;
  counts: { all: number; active: number; deleted: number; free: number; paid: number };
};

const PAGE_SIZE = 25;

const TYPE_CHIPS: { key: PublicationType; label: string; count: keyof ListResponse["counts"] }[] = [
  { key: "all", label: "All", count: "all" },
  { key: "free", label: "Free", count: "free" },
  { key: "paid", label: "Paid", count: "paid" },
];

const STATUS_CHIPS: { key: PublicationStatus; label: string; count: keyof ListResponse["counts"] }[] = [
  { key: "all", label: "Any status", count: "all" },
  { key: "active", label: "Active", count: "active" },
  { key: "deleted", label: "Deleted", count: "deleted" },
];

function formatDate(value: string | null, withTime = false) {
  if (!value) return "—";
  const options: Intl.DateTimeFormatOptions = withTime
    ? { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "short", year: "numeric" };
  return new Date(value).toLocaleString("en-GB", options);
}

// Photos are either absolute (Cloudinary) or stored locally under /uploads on the API host
function photoSrc(url: string) {
  return url.startsWith("/") ? `${getAPIBaseURL()}${url}` : url;
}

function typeLabel(type: string) {
  return type === "payant" ? "Paid" : "Free";
}

function apiGet<T>(path: string): Promise<T> {
  return fetch(`${getAPIBaseURL()}${path}`, { headers: getAdminHeaders() }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.message ?? `Request failed (${response.status})`);
    return payload as T;
  });
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Button
      variant={active ? "default" : "outline"}
      size="sm"
      className={cn("rounded-full", !active && "text-muted-foreground")}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export function PublicationsPage() {
  const [type, setType] = React.useState<PublicationType>("all");
  const [status, setStatus] = React.useState<PublicationStatus>("all");
  const [query, setQuery] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [limit, setLimit] = React.useState(PAGE_SIZE);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const list = useQuery({
    queryKey: ["publications", type, status, search, limit],
    queryFn: () =>
      apiGet<ListResponse>(
        `/api/v1/admin/publications?type=${type}&status=${status}&q=${encodeURIComponent(search)}&limit=${limit}`
      ),
  });

  if (selectedId) {
    return <PublicationDetail id={selectedId} onBack={() => setSelectedId(null)} />;
  }

  const rows = list.data?.publications ?? [];
  const total = list.data?.total ?? 0;
  const counts = list.data?.counts;

  return (
    <div className="space-y-6">
      <PageHeader title="Publications" description="Every publication shared in the app, with its author and invitation activity." />

      <Card className="rounded-2xl">
        <CardHeader className="space-y-4 border-b p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              {TYPE_CHIPS.map((chip) => (
                <Chip key={chip.key} active={type === chip.key} onClick={() => setType(chip.key)}>
                  {chip.label}
                  {counts && <span className="ml-1.5 opacity-70">{counts[chip.count].toLocaleString("en-GB")}</span>}
                </Chip>
              ))}
              <span className="mx-1 hidden w-px bg-border md:block" />
              {STATUS_CHIPS.map((chip) => (
                <Chip key={chip.key} active={status === chip.key} onClick={() => setStatus(chip.key)}>
                  {chip.label}
                  {counts && <span className="ml-1.5 opacity-70">{counts[chip.count].toLocaleString("en-GB")}</span>}
                </Chip>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search description or author…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {list.isError ? (
            <div className="flex flex-col gap-3 p-5 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between">
              <span>Publications could not be loaded. {list.error instanceof Error ? list.error.message : ""}</span>
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => list.refetch()}>
                Try again
              </Button>
            </div>
          ) : list.isLoading ? (
            <div className="space-y-3 p-5">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">No publications match these filters.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16" />
                  <TableHead>Description</TableHead>
                  <TableHead>Author</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Invitations</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((pub) => (
                  <TableRow key={pub.id} className="cursor-pointer" onClick={() => setSelectedId(pub.id)}>
                    <TableCell>
                      <img src={photoSrc(pub.photo_url)} alt="" className="size-12 rounded-lg object-cover bg-muted" loading="lazy" />
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <p className="line-clamp-2 text-sm">{pub.description}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm font-medium">{pub.author_name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{pub.author_email}</p>
                    </TableCell>
                    <TableCell>{typeLabel(pub.invitation_type)}</TableCell>
                    <TableCell className="text-right tabular-nums">{pub.invitation_count}</TableCell>
                    <TableCell>
                      <span className={cn("text-sm", pub.status === "active" ? "text-success" : "text-muted-foreground")}>
                        {pub.status === "active" ? "Active" : "Deleted"}
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{formatDate(pub.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {!list.isLoading && !list.isError && rows.length > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {rows.length.toLocaleString("en-GB")} of {total.toLocaleString("en-GB")}
          </span>
          {rows.length < total && (
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setLimit((n) => n + PAGE_SIZE)}>
              Show more
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function PublicationDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const pub = useQuery({
    queryKey: ["publication", id],
    queryFn: () => apiGet<Publication>(`/api/v1/admin/publications/${id}`),
  });

  return (
    <div className="space-y-6">
      <Button variant="outline" size="sm" className="rounded-xl" onClick={onBack}>
        <ArrowLeft className="size-4" />
        Back to publications
      </Button>

      {pub.isLoading && <Skeleton className="h-96 w-full rounded-2xl" />}

      {pub.isError && (
        <Card className="rounded-2xl border-destructive/40">
          <CardContent className="flex flex-col gap-3 p-5 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between">
            <span>This publication could not be loaded. {pub.error instanceof Error ? pub.error.message : ""}</span>
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => pub.refetch()}>
              Try again
            </Button>
          </CardContent>
        </Card>
      )}

      {pub.data && (
        <div className="grid gap-4 lg:grid-cols-5">
          <Card className="overflow-hidden rounded-2xl lg:col-span-3">
            <img src={photoSrc(pub.data.photo_url)} alt="" className="max-h-[32rem] w-full bg-muted object-contain" />
          </Card>

          <div className="space-y-4 lg:col-span-2">
            <Card className="rounded-2xl">
              <CardContent className="space-y-4 p-5">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
                      pub.data.status === "active" ? "bg-success-soft text-success" : "bg-muted text-muted-foreground"
                    )}
                  >
                    {pub.data.status === "active" ? "Active" : "Deleted"}
                  </span>
                  <span className="inline-flex rounded-full bg-muted px-2.5 py-1 text-xs font-medium">
                    {typeLabel(pub.data.invitation_type)}
                  </span>
                </div>
                <p className="whitespace-pre-line text-sm text-foreground">{pub.data.description}</p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl">
              <CardHeader className="border-b p-5">
                <h3 className="font-semibold">Details</h3>
              </CardHeader>
              <CardContent className="px-5">
                <dl className="divide-y">
                  <DetailRow label="Author" value={pub.data.author_name || "—"} />
                  <DetailRow label="Author email" value={pub.data.author_email} />
                  <DetailRow label="Invitations" value={pub.data.invitation_count.toLocaleString("en-GB")} />
                  <DetailRow label="Published flag" value={pub.data.is_published ? "Yes" : "No"} />
                  <DetailRow label="Created" value={formatDate(pub.data.created_at, true)} />
                  <DetailRow label="Updated" value={formatDate(pub.data.updated_at, true)} />
                  <DetailRow label="Publication ID" value={<span className="font-mono text-xs">{pub.data.id}</span>} />
                </dl>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 py-3">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="break-all text-right text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}
